// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { MoreVert } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { fmt } from '../../utils/common';
import { ActionNeededIndicator } from '../ActionNeededIndicator';
import PartyDisplay from '../PartyDisplay';
import { StateCell, Status } from '../StateCell';
import { RequestState, RowNew } from './types';

export const createColumns: () => GridColDef<RowNew>[] = () => {
  return [
    {
      field: 'state',
      headerName: 'State',
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <ActionNeededIndicator
              isOffered={
                params.row.requestState !== RequestState.Rejected &&
                params.row.requestState !== RequestState.Executed
              }
              receiver={params.row.receiver}
            />
            <StateCell state={params.row.requestState as Status} reason={params.row.reason} />
          </Box>
        );
      },
      flex: 0.5,
      minWidth: 50,
      filterable: true,
      valueGetter: (params) => params.row.requestState,
    },
    {
      field: 'sender',
      headerName: 'Sender',
      renderCell: (params) => <PartyDisplay party={params.row.sender} />,
      flex: 1,
      filterable: true,
      valueGetter: (params) => params.row.sender,
    },
    {
      field: 'receiver',
      headerName: 'Receiver',
      renderCell: (params) => <PartyDisplay party={params.row.receiver} />,
      flex: 1,
      filterable: true,
      valueGetter: (params) => params.row.receiver,
    },
    {
      field: 'instrumentId',
      headerName: 'Instrument Id',
      valueGetter: (params) => params.row.instrumentId,
      flex: 1,
      filterable: true,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      renderCell: (params) => fmt(params.row.amount, 2),
      valueGetter: (params) => Number(params.row.amount),
      flex: 1,
      filterable: true,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.registrar} />,
      flex: 1,
      filterable: true,
      valueGetter: (params) => params.row.registrar,
    },
    {
      field: 'reference',
      headerName: 'Reference',
      valueGetter: (params) => params.row.reference,
      flex: 1,
      filterable: true,
    },
    {
      field: 'createdAt',
      headerName: 'createdAt',
      sortable: true,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="caption" fontFamily="monospace">
          {new Date(params.row.createdAt).toLocaleString()}
        </Typography>
      ),
      minWidth: 100,
      flex: 1,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: true,
      align: 'right',
      renderCell: () => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <MoreVert />
        </Box>
      ),
      minWidth: 50,
      flex: 0.1,
      filterable: false,
    },
  ];
};
